import { App, URLOpenListenerEvent } from '@capacitor/app';
import { RequireAuth } from 'lincd-auth/components/RequireAuth';
import React, { Fragment, lazy, Suspense, useEffect, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { Spinner } from './components/atoms/Spinner.js';
import { DefaultLayout } from './layout/DefaultLayout.js';
import PageNotFound from './pages/PageNotFound.js';
import Signin from './pages/Signin.js';

//In React 18 you can use 'lazy' to import pages only when you need them.
//This will cause webpack to create multiple bundles, and the right bundles are automatically loaded
interface RouteObj {
  path: string;
  component?: React.LazyExoticComponent<() => JSX.Element>;
  render?: () => JSX.Element;
  requireAuth?: boolean;
  excludeFromMenu?: boolean;
  useSSR?: boolean; // if true, the route will be rendered on the server side
  /**
   * only if not using SSR, this function will be called to render the loader/spinner
   */
  serverRender?: () => any;
  label?: string;
}

// Create a wrapper function for lazy imports that adds logging and timeout
const lazyWithLogging = (
  importFn: () => Promise<any>,
  componentName: string
) => {
  return lazy(() => {
    console.log(`[${componentName}] Starting lazy import...`);
    const startTime = performance.now();
    let finishedLoading = false;

    // Create timeout promise
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        if (!finishedLoading) {
          console.warn(
            `[${componentName}] Lazy import timeout after 3 seconds`
          );
        }
      }, 3000);
    });

    // Create import promise
    const importPromise = importFn().then((module) => {
      finishedLoading = true;
      const loadTime = (performance.now() - startTime).toFixed(2);
      console.log(
        `[${componentName}] Lazy import finished successfully (${loadTime}ms)`
      );
      return module;
    });

    // Race between timeout and actual import
    return Promise.race([importPromise, timeoutPromise]).catch((error) => {
      console.error(`[${componentName}] Lazy import failed:`, error);
      return importPromise; // Still return the original import even if timeout occurred
    });
  });
};

export const ROUTES = {
  index: {
    path: '/',
    component: lazyWithLogging(
      () => import('./pages/ActionsOverview.js'),
      'ActionsOverview'
    ),
    label: 'Home',
    requireAuth: true,
  },
  signin: {
    path: '/signin',
    // component: lazy(() => import('./pages/Signin' )),
    component: Signin,
    label: 'Sign In',
    serverRender: () => (
      <DefaultLayout backgroundColor={'dark'}>
        <Spinner centered={true} color={'white'} />
      </DefaultLayout>
    ),
  },
  team_action: {
    path: '/team-action',
    component: lazyWithLogging(
      () => import('./pages/TeamAction'),
      'TeamAction'
    ),
    label: 'Team Action',
    requireAuth: true,
  },
  action: {
    path: '/action/:id',
    component: lazyWithLogging(() => import('./pages/Action'), 'Action'),
    label: 'Action',
    requireAuth: true,
  },
  admin: {
    path: '/admin',
    component: lazyWithLogging(() => import('./pages/Admin'), 'Admin'),
    label: 'Admin',
    requireAuth: true,
  },
  users: {
    path: '/admin/users',
    component: lazyWithLogging(() => import('./pages/Users'), 'Users'),
    label: 'Users',
    requireAuth: true,
  },
  user: {
    path: '/admin/users/:accountId',
    component: lazyWithLogging(() => import('./pages/User'), 'User'),
    label: 'User',
    requireAuth: true,
  },
  duplicate_account: {
    path: '/admin/duplicate-account',
    component: lazyWithLogging(
      () => import('./pages/DuplicateAccount'),
      'DuplicateAccount'
    ),
    label: 'Duplicate Account',
    requireAuth: true,
  },
  events: {
    path: '/admin/events',
    component: lazy(() => import('./pages/Events')),
    label: 'Events',
    requireAuth: true,
  },
  teams: {
    path: '/admin/teams',
    component: lazy(() => import('./pages/Teams')),
    label: 'Teams',
    requireAuth: true,
  },
  join_team: {
    path: '/join-team',
    component: lazyWithLogging(() => import('./pages/JoinTeam'), 'JoinTeam'),
    label: 'Join Team',
    // requireAuth: true,
  },
  register: {
    path: '/register',
    component: lazyWithLogging(() => import('./pages/Register'), 'Register'),
    label: 'Register',
    requireAuth: false,
  },
  register_email: {
    path: '/register/email',
    component: lazyWithLogging(
      () => import('./pages/RegisterEmail'),
      'RegisterEmail'
    ),
    label: 'Register with Email',
    excludeFromMenu: true,
    requireAuth: false,
  },
  invite_your_team: {
    path: '/invite-your-team',
    component: lazyWithLogging(
      () => import('./pages/InviteYourTeam'),
      'InviteYourTeam'
    ),
    label: 'Invite Your Team',
    excludeFromMenu: true,
    requireAuth: true,
  },
  get_started: {
    path: '/get-started',
    component: lazyWithLogging(
      () => import('./pages/GetStarted'),
      'GetStarted'
    ),
    label: 'Get Started',
    excludeFromMenu: true,
    requireAuth: true,
  },
  attendence_select: {
    path: '/attendence-select',
    component: lazyWithLogging(
      () => import('./pages/AttendanceSelect'),
      'AttendanceSelect'
    ),
    label: 'Attendence Select',
    excludeFromMenu: true,
    // requireAuth: true,
  },
  resources: {
    path: '/resources',
    component: lazyWithLogging(() => import('./pages/Resources'), 'Resources'),
    label: 'Resources',
    requireAuth: true,
  },
  resource_detail: {
    path: '/resource/:id',
    component: lazyWithLogging(
      () => import('./pages/ResourceDetail'),
      'ResourceDetail'
    ),
    label: 'Resource Detail',
    excludeFromMenu: true,
    requireAuth: true,
  },
  poet: {
    path: '/team',
    component: lazyWithLogging(() => import('./pages/Team'), 'Team'),
    label: 'Team',
    requireAuth: true,
  },
  start_team: {
    path: '/start-team',
    component: lazyWithLogging(() => import('./pages/StartTeam'), 'StartTeam'),
    label: 'Start Team',
    excludeFromMenu: true,
    requireAuth: true,
  },
  live_gameboard: {
    path: '/live-gameboard',
    component: lazy(() => import('./pages/LiveGameboard')),
    label: 'Live Gameboard',
    excludeFromMenu: true,
  },
  profile: {
    path: '/profile',
    component: lazyWithLogging(() => import('./pages/Profile'), 'Profile'),
    label: 'Profile',
    requireAuth: true,
  },
  globalGameBoard: {
    path: '/global-game-board',
    component: lazyWithLogging(
      () => import('./pages/GlobalGameBoard'),
      'GlobalGameBoard'
    ),
    label: 'Global Game Board',
    excludeFromMenu: true,
    requireAuth: true,
  },
  contact: {
    path: '/contact',
    component: lazyWithLogging(() => import('./pages/Contact'), 'Contact'),
    label: 'Contact',
  },
  peace_flame: {
    path: '/peace-flame',
    component: lazyWithLogging(
      () => import('./pages/PeaceFlame'),
      'PeaceFlame'
    ),
    label: 'Peace Flame',
    excludeFromMenu: true,
    requireAuth: false,
  },
  terms: {
    path: '/terms',
    component: lazyWithLogging(
      () => import('./pages/TermsOfUse'),
      'TermsOfUse'
    ),
    label: 'Terms',
  },
  privacy_policy: {
    path: '/privacy-policy',
    component: lazyWithLogging(
      () => import('./pages/PrivacyPolicy'),
      'PrivacyPolicy'
    ),
    label: 'Privacy Policy',
  },
  teams_overview: {
    path: '/teams-overview',
    component: lazyWithLogging(
      () => import('./pages/TeamsOverview'),
      'TeamsOverview'
    ),
    label: 'Teams Overview',
    requireAuth: true,
  },
  reset_password_callback: {
    path: '/auth/reset-password',
    component: lazyWithLogging(
      () => import('lincd-auth/components/ForgotPasswordCallback'),
      'ForgotPasswordCallback'
    ),
  },
};

export default function AppRoutes() {
  const navigate = useNavigate();
  // check deeplink
  useEffect(() => {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      // Example url: https://beerswift.com/tabs/tab2
      // slug = /tabs/tab2
      const slug = event.url.replace(process.env.SITE_ROOT, '');
      console.log('AppUrlListener: ', slug);

      if (slug) {
        navigate(slug);
      }
    });
  }, []);

  return (
    <Routes>
      {Object.keys(ROUTES).map((routeName) => {
        const route = ROUTES[routeName];

        //if a route is marked as requireAuth, wrap it in the RequireAuth component and pass the signinRoute
        const AuthGuard = route.requireAuth ? RequireAuth : Fragment;
        const authProps = route.requireAuth
          ? {
              signinRoute: ROUTES.signin.path,
              renderLoading: () => (
                <Suspense>
                  <DefaultLayout>
                    <Spinner centered={true} />
                  </DefaultLayout>
                </Suspense>
              ),
            }
          : {};

        return (
          <Route
            key={route.path}
            path={route.path}
            element={
              <AuthGuard {...authProps}>
                <Suspense fallback={<Spinner centered={true} />}>
                  <RenderRoute routeName={routeName} route={route} />
                </Suspense>
              </AuthGuard>
            }
          />
        );
      })}
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
}
// define a render function that determines what to render based on the component and route.render
const RenderRoute = ({ route, routeName }) => {
  let navigate = useNavigate();
  const onPasswordIsReset = () => {
    navigate(ROUTES.index.path);
  };

  //added support to only render the spinner on the first render if the route is marked as preventSSR
  let [client, setClient] = useState<any>(null);
  useEffect(() => {
    setClient(true);
  }, []);

  if (!route.useSSR && !client) {
    return route.serverRender ? (
      route.serverRender()
    ) : (
      <DefaultLayout>
        <Spinner centered={true} />
      </DefaultLayout>
    );
  }

  // if a Component is defined, render it using JSX syntax (<Component />)
  // if not, check if a route.render function is defined and call that render function if available.
  // if neither Component nor route.render is defined, return null
  const Component = route.component;
  return Component ? (
    <Component
      {...(routeName === 'reset_password_callback' && {
        onPasswordIsReset: onPasswordIsReset,
      })}
    />
  ) : route.render ? (
    route.render()
  ) : null;
};
